<template>
    <div class="row" v-bind:class="{ 'has-danger' : error !== null, 'form-group': !noFormGroup }">
        <label :class="labelClasses" class="form-control-label  col-form-label" :for="name">{{ labelComputed }}</label>
        <div :class="inputHolderClasses">
            <div class="document-editor">
                <div class="document-editor__toolbar" ref="toolbar_container"></div>
                <div class="document-editor__editable-container">
                    <div class="document-editor__editable" :name="name" ref="editor"></div>
                </div>
            </div>
        </div>
        <span class="help-block form-control-feedback" v-if="error != null"
              v-text="error"></span>

        <b-modal id="add_data_element_modal" :hideFooter="true" :title="uLang('app.add_data_element')" ref="add_data_element_modal" size="lg" @hidden="onDataElementModalHidden">
            <div class="clearfix">
                <backend-select
                    :route="route('action-document-data-elements.modules.index')"
                    name="module"
                    object-name-key="display_name"
                    v-model="data_element_module"
                    return="name"
                    track-by="name"
                />

                <form-select track-by="id" v-model="data_element" :options="data_elements" name="data_element"
                             object-name-key="display_name" :loading="!data_elements.length" :disabled="!data_elements.length"/>

                <div class="pull-right">
                    <a class="btn btn-brand" @click.prevent="addDataElement">{{ uLang('app.add') }}</a>
                </div>
            </div>
        </b-modal>

        <b-modal id="add_form_element_modal" :hideFooter="true" :title="uLang('app.add_form_element')" ref="add_form_element_modal" size="lg" @hidden="onFormElementModalHidden">
            <div class="clearfix">
                <backend-select :route="route('action-document-field-elements.index')" name="form_element" object-name-key="name_translated" v-model="form_element"/>
                <div class="form-group row">
                    <label
                        class="form-control-label col-form-label col-md-3 col-xl-2 col-sm-12"
                        for="form_element_hint"
                    >{{ uLang('app.hint_for_coach') }}:</label>
                    <div class="col-md-9 col-xl-10 col-sm-12">
                        <textarea
                            class="form-control"
                            name="form_element_hint"
                            rows="4"
                            v-model="form_element_hint"
                        ></textarea>
                    </div>
                </div>
                <div class="pull-right">
                    <a class="btn btn-brand" @click.prevent="addFormElement">{{ uLang('app.add') }}</a>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import ActionDocumentEditor from '@ckeditor/ckeditor5-build-decoupled-document'
    import DutchLanguage from '@ckeditor/ckeditor5-build-decoupled-document/build/translations/nl'
    import ElementMixin from '~/mixins/element'
    import ButtonPlugin from '~/components/plugins/ckeditor5/ck-editor-plugins/ButtonPlugin';
    import SeparatorPlugin from '~/components/plugins/ckeditor5/ck-editor-plugins/SeparatorPlugin';
    import UploadAdapter from '~/adapters/CkEditorUploadAdapter'

    export default {
        name: 'Ckeditor',
        mixins: [
            ElementMixin,
        ],

        props: {
            inputHolderClasses: {
                type: String,
                required: false,
                default: 'col-md-9 col-xl-10 col-sm-12'
            },
            labelClasses: {
                type: String,
                required: false,
                default: 'col-md-3 col-xl-2 col-sm-12',
            },
            noFormGroup: {
                type: Boolean,
                required: false,
                default: false,
            },
            readonly: {
                type: Boolean,
                required: false,
                default: false,
            },
            value: {
                required: false,
                default: '<p>&nbsp;</p>', // Is default value of a new created editor
            }
        },

        data: function () {
            return {
                instance: null,
                data_element_module: null,
                data_elements: [],
                data_element: null,
                form_element: null,
                form_element_hint: '',
            };
        },

        watch: {
            value (newVal, oldVal) {
                let html = this.instance.getData();
                if (html !== this.value) {
                    this.instance.setData(this.value);
                }
            },

            readonly(newVal, oldVal) {
                this.instance.isReadOnly = newVal;
            },

            data_element_module(newVal, oldVal) {
                this.retrieveDataElements(newVal);
            },
        },

        mounted () {
            ActionDocumentEditor
                .create(this.$refs.editor, {
                    language: 'nl',
                    highlight: {
                        options: [
                            {
                                model: 'yellowMarker',
                                class: 'marker-yellow',
                                title: 'Yellow marker',
                                color: 'var(--ck-highlight-marker-yellow)',
                                type: 'marker'
                            },
                            {
                                model: 'pinkPen',
                                class: 'pen-pink',
                                title: 'Roze pen',
                                color: '#c1006d',
                                type: 'pen'
                            },
                            {
                                model: 'purplePen',
                                class: 'pen-purple',
                                title: 'Paarse pen',
                                color: '#210861',
                                type: 'pen'
                            }
                        ]
                    }
                })
                .then(editor => {
                    this.instance = editor;
                    editor.setData(this.value);
                    editor.isReadOnly = this.readonly;
                    this.$refs.toolbar_container.appendChild( editor.ui.view.toolbar.element );

                    editor.plugins.get( 'FileRepository' ).createUploadAdapter = loader => {
                        return new UploadAdapter(
                            loader,
                            route('files.store', {'requestUrl': 1 }),
                            editor.t
                        );
                    };
                    
                    editor.model.document.on('change', () => {
                        this.updateValue(editor.getData());
                    });

                    this.addElementButtons();
                })
                .catch(error => {
                    console.error(error);
                });
        },

        methods: {
            updateValue: function (value) {
                this.$emit('input', value);
            },

            async retrieveDataElements(data_element_module) {
                if (data_element_module === null) return;

                const { data } = await window.axios.get(route('action-document-data-elements.index', { module: data_element_module }));
                this.data_elements = _.sortBy(data, ['display_name']);
            },

            addElementButtons() {
                // Create separator
                let separator = new SeparatorPlugin().node();

                // Create button
                let dataElementBtn = new ButtonPlugin('<i class="fa fa-2x fa-vcard-o"></i><span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">' + uLang('app.add_data_element') + '</span></span>').node();
                let formElementBtn = new ButtonPlugin('<i class="fa fa-2x fa-edit"></i><span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">' + uLang('app.add_form_element') + '</span></span>').node();
                let pageBreakBtn = new ButtonPlugin('<span class="fa-stack">' +
                            '<i class="fa fa-file fa-stack-2x"></i>' +
                            '<i class="fa fa-plus fa-stack-1x fa-inverse"></i>' +
                        '</span>' +
                    '<span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">' + uLang('app.add_page_break') + '</span></span>').node();
                let insertLogsBtn = new ButtonPlugin('<i class="fa fa-2x fa-comments"></i>' +
                    '<span class="ck ck-tooltip ck-tooltip_s"><span class="ck ck-tooltip__text">' + uLang('app.add_reporting_logs') + '</span></span>').node();

                // Add button group
                this.$refs.toolbar_container.children[0].append(separator);
                this.$refs.toolbar_container.children[0].append(dataElementBtn);
                this.$refs.toolbar_container.children[0].append(formElementBtn);
                this.$refs.toolbar_container.children[0].append(pageBreakBtn);
                this.$refs.toolbar_container.children[0].append(insertLogsBtn);

                // Add event listeners
                dataElementBtn.addEventListener('click', () => {
                    this.$refs.add_data_element_modal.show();
                });

                formElementBtn.addEventListener('click', () => {
                    this.$refs.add_form_element_modal.show();
                });

                pageBreakBtn.addEventListener('click', () => {
                    this.addPageBreak();
                });

                insertLogsBtn.addEventListener('click', () => {
                    this.insertLogs();
                });
            },

            addDataElement() {
                if (this.data_element !== null) {
                    this.instance.model.change(writer => {
                        writer.insertText('[data.' + this.data_element.module + '.' + this.data_element.name + ']', this.instance.model.document.selection.getFirstPosition());
                    });
                }

                this.$refs.add_data_element_modal.hide();
            },

            addFormElement() {
                if (this.form_element !== null) {
                    this.instance.model.change(writer => {
                        let textToInsert = `[form.${this.form_element.name}.${window.randomString(10)}]`

                        if(this.form_element_hint.trim() !== '') {
                            textToInsert = `[[${this.form_element_hint}]]((${textToInsert}))`
                        }

                        writer.insertText(textToInsert, this.instance.model.document.selection.getFirstPosition());
                    });
                }

                this.$refs.add_form_element_modal.hide();
            },

            addPageBreak() {
                this.instance.model.change(writer => {
                    const textToInsert = '[data.page_break]';

                    writer.insertText(textToInsert, this.instance.model.document.selection.getFirstPosition());
                });
            },

            insertLogs() {
                this.instance.model.change(writer => {
                    const textToInsert = '[extra.reporting_logs]';

                    writer.insertText(textToInsert, this.instance.model.document.selection.getFirstPosition());
                });
            },

            onDataElementModalHidden() {
                this.data_element_module = null;
                this.data_elements = [];
                this.data_element = null;
            },

            onFormElementModalHidden() {
                this.form_element = null;
                this.form_element_hint = '';
            },

            ckCallback(args) {
                return (new Date()).getTime();
            },
        },
    }
</script>

<style lang="scss">
    #add_data_element_modal .modal-content {
        overflow-y: visible;
    }

    .document-editor .ck {
        .fa.fa-2x {
            font-size: 1.5em !important;
        }

        .fa-stack {
            position: relative;

            .fa-stack-2x.fa-file {
                font-size: 1.5em !important;
            }

            .fa-stack-1x.fa-plus {
                left: 5px;
                top: 10px;
                position: absolute;
                color: #fff !important;
            }
        }
    }
</style>
